import {
	ApiStatusError,
	createCloudSharedVolumeFile,
	createCloudSharedVolumeFolder,
	deleteCloudSharedVolumeEntries as apiDeleteCloudSharedVolumeEntries,
	moveCloudSharedVolumeEntry,
} from "@/lib/api";
import {
	cloudSharedVolumeMovablePaths,
	cloudSharedVolumeRowKey,
	cloudSharedVolumeTopLevelPaths,
	isCloudSharedVolumeDirectoryLoaded,
} from "@/lib/cloud-shared-volumes";
import { pruneOpenEnvironmentFileTabs, splitPath } from "@/lib/environment-files";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import { useCloudSharedVolumeStore } from "@/stores/useCloudSharedVolumeStore";
import { useToastStore } from "@/stores/useToastStore";

const REFRESH_FAILED = "共有ボリュームを取り直せませんでした。";

function joinPath(directory: string, name: string): string {
	return directory === "" ? name : `${directory}/${name}`;
}

function isCurrent(environmentId: string): () => boolean {
	return () => useCloudSharedVolumeStore.getState().environmentId === environmentId;
}

/**
 * 変えた後に、関わったディレクトリを取り直す。removed (消えた・移したもの) の下の読み込んだディレクトリは捨て、
 * 開いているタブは消えたファイルのものを閉じる (svf_ の ID はパスから作るので、名前を変えても閉じる)。
 */
function refreshAfterChange(
	environmentId: string,
	volumeId: string,
	directories: readonly string[],
	removed: readonly string[],
): void {
	const current = isCurrent(environmentId);
	if (!current()) return;
	const store = useCloudSharedVolumeStore.getState();
	store.forgetDirectories(volumeId, removed);
	const refresh = async () => {
		await Promise.all(
			[...new Set(directories)].map((directory) => store.reloadDirectory(volumeId, directory)),
		);
		if (removed.length > 0) await pruneOpenEnvironmentFileTabs(environmentId, current);
	};
	reportSharedVolumeFailure(refresh(), REFRESH_FAILED);
}

export const CREATE_TARGET_MISSING = "作成先のフォルダが見つかりません。";

/**
 * directory (根からのパス) に空のファイルかフォルダを作る。directory が取り直しで消えていれば作らない (無い親は作られて
 * しまうため)。失敗はダイアログに出すので、知らせずに投げる。
 */
export async function createCloudSharedVolumeEntry(
	environmentId: string,
	volumeId: string,
	directory: string,
	name: string,
	kind: "file" | "folder",
): Promise<void> {
	const { volumes, directories } = useCloudSharedVolumeStore.getState();
	if (!isCloudSharedVolumeDirectoryLoaded(volumes, directories, volumeId, directory))
		throw new Error(CREATE_TARGET_MISSING);
	const create = kind === "folder" ? createCloudSharedVolumeFolder : createCloudSharedVolumeFile;
	await create(environmentId, volumeId, joinPath(directory, name));
	if (isCurrent(environmentId)()) {
		useToastStore.getState().show({
			type: "success",
			message: kind === "folder" ? "フォルダを作成しました。" : "ファイルを作成しました。",
		});
	}
	refreshAfterChange(environmentId, volumeId, [directory], []);
}

/** 空のフォルダを作る (フォルダを置くとき)。同じ名前のフォルダが既にあれば何もせず、ファイルなどがあれば投げる。 */
export async function ensureCloudSharedVolumeFolder(
	environmentId: string,
	volumeId: string,
	path: string,
): Promise<void> {
	await createCloudSharedVolumeFolder(environmentId, volumeId, path, true);
}

/** 同じフォルダの中で名前を変える。失敗はダイアログに出すので、知らせずに投げる。 */
export async function renameCloudSharedVolumeEntry(
	environmentId: string,
	volumeId: string,
	path: string,
	newName: string,
): Promise<void> {
	const { dir, name } = splitPath(path);
	if (newName === name) return;
	await moveCloudSharedVolumeEntry(environmentId, volumeId, path, joinPath(dir, newName));
	if (isCurrent(environmentId)()) {
		useToastStore.getState().show({ type: "success", message: "名前を変更しました。" });
	}
	refreshAfterChange(environmentId, volumeId, [dir], [path]);
}

/**
 * 同じボリュームの中で destination (根からのパス) へ移す。移動先に同じ名前があれば上書きせずに断る。結果はトーストで知らせ、
 * 失敗は投げる。
 */
export async function moveCloudSharedVolumeEntries(
	environmentId: string,
	volumeId: string,
	paths: readonly string[],
	destination: string,
): Promise<void> {
	const toast = useToastStore.getState();
	const sources = cloudSharedVolumeMovablePaths(paths, destination);
	if (sources.length === 0) {
		toast.show({ type: "info", message: "移動対象がありませんでした。" });
		return;
	}
	const names = sources.map((source) => splitPath(source).name);
	const loaded = useCloudSharedVolumeStore
		.getState()
		.directories.get(cloudSharedVolumeRowKey(volumeId, destination));
	const collision =
		names.find((name, index) => names.indexOf(name) !== index) ??
		names.find((name) => loaded?.entries.some((entry) => entry.name === name));
	if (collision !== undefined) {
		toast.show({ type: "error", message: `移動先に「${collision}」が既に存在します。` });
		return;
	}

	let moved = 0;
	try {
		for (; moved < sources.length; moved += 1) {
			await moveCloudSharedVolumeEntry(
				environmentId,
				volumeId,
				sources[moved],
				joinPath(destination, names[moved]),
			);
		}
		if (isCurrent(environmentId)()) {
			toast.show({
				type: "success",
				message: sources.length === 1 ? "移動しました。" : `${sources.length}件を移動しました。`,
			});
		}
	} catch (err) {
		if (isCurrent(environmentId)()) {
			const reason = err instanceof ApiStatusError ? err.message : "移動に失敗しました。";
			toast.show({
				type: "error",
				message:
					moved === 0
						? reason
						: `${moved}/${sources.length}件を移動した時点で失敗しました。${reason}`,
			});
		}
		throw err;
	} finally {
		if (moved > 0) {
			const done = sources.slice(0, moved);
			refreshAfterChange(
				environmentId,
				volumeId,
				[destination, ...done.map((source) => splitPath(source).dir)],
				done,
			);
		}
	}
}

/** 消す (一番上のものだけ送る)。失敗はダイアログに出すので、知らせずに投げる。途中まで消えていることもあるので取り直す。 */
export async function deleteCloudSharedVolumeEntries(
	environmentId: string,
	volumeId: string,
	paths: readonly string[],
): Promise<void> {
	const topLevel = cloudSharedVolumeTopLevelPaths(paths);
	if (topLevel.length === 0) return;
	try {
		await apiDeleteCloudSharedVolumeEntries(environmentId, volumeId, topLevel);
		if (isCurrent(environmentId)()) {
			useToastStore.getState().show({
				type: "success",
				message: topLevel.length === 1 ? "削除しました。" : `${topLevel.length}件を削除しました。`,
			});
		}
	} finally {
		refreshAfterChange(
			environmentId,
			volumeId,
			topLevel.map((path) => splitPath(path).dir),
			topLevel,
		);
	}
}
